import * as messagingManagementsActions from "../actions/Management/messagingManagementsActions";
import Logger from "./Logger";
import * as requestActions from "../actions/requestActions";
import { getSignalingUrl } from "../utils/messagingUrlFactory";
import * as callActions from "../actions/callActions";

let io;
let store;
let intl;

const logger = new Logger("MessagingClient");

export default class MessagingClient {
  /**
   * @param {Object} data
   * @param {Object} data.store - The Redux store.
   * @param {Object} data.intl - react-intl object
   */

  static init(data) {
    store = data.store;
    intl = data.intl;
  }

  constructor({ userToken } = {}) {
    this._signalingUrl = null;
    this._userToken = userToken;
    this._soundAlert = new Audio("/sounds/notify.ogg");
    this._signalingSocket = null;
  }

  timeoutCallback(callback) {
    let called = false;

    const interval = setTimeout(() => {
      if (called) return;
      called = true;
      callback(new Error("Request timeout."));
    }, 30000);

    return (...args) => {
      if (called) return;
      called = true;
      clearTimeout(interval);

      callback(...args);
    };
  }

  async _loadDynamicImports() {
    ({ default: io } = await import(
      /* webpackPrefetch: true */
      /* webpackChunkName: "socket.io" */
      "socket.io-client"
    ));
  }

  _soundNotification() {
    if (!store.getState().settings.soundNotificationsDisabled) {
      const alertPromise = this._soundAlert.play();

      if (alertPromise !== undefined) {
        alertPromise.then().catch((error) => {
          logger.error("_soundAlert.play() | failed: %o", error);
        });
      }
    }
  }

  _desktopAttention({ type, title, body }) {
    try {
      const desktop =
        typeof window !== "undefined" ? window.wunityDesktop : null;
      if (desktop && typeof desktop.notifyAttention === "function") {
        desktop.notifyAttention({ type, title, body });
      }
    } catch (error) {
      logger.error("_desktopAttention | failed: %o", error);
    }
  }

  _messagePreview(payload) {
    const contentType = payload?.payload?.contentType;
    const content = payload?.payload?.content;
    if (contentType === "text" && typeof content === "string" && content.trim()) {
      return content.trim().slice(0, 140);
    }
    if (contentType === "image") return "Fotoğraf";
    if (contentType === "video") return "Video";
    if (contentType === "audio") return "Sesli mesaj";
    if (contentType === "file") return "Dosya";
    if (contentType === "location") return "Konum";
    return intl.formatMessage({
      id: "messaging.haveNewMessage",
      defaultMessage: "You have new message.",
    });
  }

  close() {
    if (this._signalingSocket) {
      try {
        this._signalingSocket.removeAllListeners();
        this._signalingSocket.close();
      } catch (e) {
        /* ignore */
      }
      this._signalingSocket = null;
    }
  }

  _normalizeConnectPresence(presenceStatus) {
    // DB'de kalan "offline" ile bağlanmak oturumu hiç kaydetmez; arama/mesaj düşmez.
    // Kullanıcı busy/away seçtiyse onu koru; offline/geçersiz → online.
    if (["online", "busy", "away"].includes(presenceStatus)) {
      return presenceStatus;
    }
    return "online";
  }

  async connect(presenceStatus = "online") {
    await this._loadDynamicImports();

    const status = this._normalizeConnectPresence(presenceStatus);
    const token = localStorage.getItem("token");
    if (!token) {
      logger.warn("MessagingClient.connect skipped: no token");
      return;
    }

    // Yeniden bağlanmadan önce eski soketi kapat — Redis'te yetim RTC oturumu birikmesin
    if (this._signalingSocket) {
      this.close();
    }

    this._signalingUrl = getSignalingUrl(token, status);
    this._signalingSocket = io(this._signalingUrl, {
      transports: ["websocket"],
      query: {
        token,
        presenceStatus: status,
      },
    });

    this._signalingSocket.on("connect", () => {
      logger.debug('signaling Peer "connect" event');
      try {
        const me = store.getState().user?.me;
        if (me && me.presenceStatus !== status) {
          store.dispatch({
            type: "SET_PRESENCE_STATUS",
            payload: { status },
          });
        }
      } catch (e) {
        /* ignore */
      }
    });

    this._signalingSocket.on("disconnect", (reason) => {
      logger.warn('signaling Peer "disconnect" event [reason:"%s"]', reason);
    });
    this._signalingSocket.on("failed", (reason) => {
      logger.warn('signaling Peer "failed" event [reason:"%s"]', reason);
    });

    this._signalingSocket.on("reconnect_failed", () => {
      logger.warn('signaling Peer "reconnect_failed" event');
    });

    this._signalingSocket.on("reconnect", (attemptNumber) => {
      logger.debug(
        'signaling Peer "reconnect" event [attempts:"%s"]',
        attemptNumber
      );
    });

    this._signalingSocket.on("packet", (packet, callback) => {
      logger.debug("signaling Peer packet event ");

      switch (packet.payloadType) {
        case "new_message":
          store.dispatch(
            messagingManagementsActions.addNewMessage(
              [packet.payload],
              true,
              packet.toId,
              store.getState().me.id !== packet.fromId
            )
          );
          if (
            packet.fromId !== store.getState().me.id &&
            packet?.payload?.payload?.contentType !== "incoming_call"
          ) {
            const isActiveChat =
              store.getState().messagingManagement.selectedChat?.id ===
              packet.toId;
            const windowHidden =
              typeof document !== "undefined" && document.hidden;

            if (!isActiveChat) {
              store.dispatch(
                requestActions.notify({
                  text: intl.formatMessage({
                    id: "messaging.haveNewMessage",
                    defaultMessage: "You have new message.",
                  }),
                })
              );
              this._soundNotification();
            }

            // Tray'de giz beliyken veya başka sohbetteyken pencereyi geri getir.
            if (!isActiveChat || windowHidden) {
              const chat = (
                store.getState().messagingManagement.chatList || []
              ).find((item) => item.id === packet.toId);
              this._desktopAttention({
                type: "message",
                title:
                  chat?.name ||
                  (typeof window !== "undefined" &&
                    window.config &&
                    (window.config.title || window.config.portalBrand)) ||
                  "Wunity",
                body: this._messagePreview(packet.payload),
              });
            }
          }

          if (packet.fromId !== store.getState().me.id)
            store.dispatch(
              messagingManagementsActions.sendAck(
                packet.toId,
                packet.payload.id,
                store.getState().me.id
              )
            );

          break;
        case "new_group_created":
          store.dispatch(
            messagingManagementsActions.addChatList([packet.payload])
          );
          break;
        case "group_updated":
          store.dispatch(messagingManagementsActions.refreshAvatar());
          store.dispatch(
            messagingManagementsActions.updateGroup(packet.payload)
          );
          break;
        case "user_avatar_updated": {
          const userId =
            packet.payload?.userId || packet.fromId || packet.toId;
          if (userId) {
            store.dispatch(
              messagingManagementsActions.invalidateUserAvatar(userId)
            );
          }
          break;
        }
        case "group_deleted":
          store.dispatch(
            messagingManagementsActions.deleteGroup(packet.payload)
          );
          break;
        case "mark_seen":
          // fromId = mesajı gören kişi; unAck/unseen listelerinden O çıkarılmalı
          store.dispatch(
            messagingManagementsActions.markAsSeen(
              packet.toId,
              packet.messageId,
              packet.fromId,
              true
            )
          );
          break;
        case "mark_ack":
          store.dispatch(
            messagingManagementsActions.sendAck(
              packet.toId,
              packet.messageId,
              packet.fromId,
              true
            )
          );
          break;
        case "deleteChatMessage":
          store.dispatch(
            messagingManagementsActions.deleteMessage(
              packet.toId,
              packet.messageId,
              true
            )
          );
          break;
        case "incoming_call":
          if (
            !store.getState().call.currentCall &&
            !store.getState().call.incomingCall &&
            !store.getState().call.outgoingCall &&
            !store.getState().room.joined
          ) {
            store.dispatch(callActions.setIncomingCall(packet));
            this._desktopAttention({
              type: "call",
              title: intl.formatMessage({
                id: "messaging.inComing",
                defaultMessage: "Incoming Call",
              }),
              body:
                packet?.payload?.group?.name ||
                (packet?.payload?.screen
                  ? intl.formatMessage({
                      id: "messaging.screenShareCall",
                      defaultMessage: "ekran paylaşımlı arama",
                    })
                  : packet?.payload?.video
                  ? intl.formatMessage({
                      id: "messaging.videoCall",
                      defaultMessage: "görüntülü arama",
                    })
                  : intl.formatMessage({
                      id: "messaging.voiceCall",
                      defaultMessage: "sesli arama",
                    })),
            });
          } else {
            store.dispatch(
              callActions.markBusyCall(
                packet.payload.group.id,
                packet.payload.roomId,
                packet.payload.video
              )
            );
          }
          break;
        case "answer_call":
          if (
            !store.getState().call.currentCall &&
            store.getState().call.outgoingCall
          ) {
            const outgoing = store.getState().call.outgoingCall;
            const isScreen = Boolean(
              outgoing?.screen || packet.payload?.screen
            );
            store.dispatch(
              callActions.setCurrentCall({
                ...packet.payload,
                screen: isScreen,
                video: Boolean(isScreen ? false : packet.payload?.video),
                // Ekran paylaşımlı aramada paylaşımı arayan başlatır
                startScreenShare: isScreen,
              })
            );
          } else {
            store.dispatch(callActions.setIncomingCall(undefined));
            store.dispatch(callActions.setCurrentCall(undefined));
          }
          break;
        case "reject_call":
          store.dispatch(callActions.setCurrentCall(undefined));
          store.dispatch(callActions.setOutgoingCall(undefined));
          break;
        case "end_call": {
          const callState = store.getState().call;

          if (
            callState.currentCall?.roomId === packet.payload.roomId ||
            (callState.incomingCall?.payload.roomId === packet.payload.roomId &&
              callState.incomingCall?.payload?.group?.type === "individual")
          ) {
            store.dispatch(callActions.setCurrentCall(undefined));
            store.dispatch(callActions.setIncomingCall(undefined));
            store.dispatch(callActions.setOutgoingCall(undefined));
          }
          break;
        }
        case "mark_busy_call":
          store.dispatch(callActions.setOutgoingCall(undefined));
          store.dispatch(
            requestActions.notify({
              text: intl.formatMessage({
                id: "messaging.userAnotherCall",
                defaultMessage: "User is on another call.",
              }),
            })
          );
        default: {
          break;
        }
      }
      callback(null);
    });
  }
}
